<template>
  <div>
    <div class="sticky-board__toolbar">
      <div class="sticky-board__title">Mural de anotações</div>
      <div class="sticky-board__subtitle">
        Arraste os stickers para organizar seu resumo da aula.
      </div>
    </div>

    <div
      ref="board"
      class="sticky-board"
      :style="{ minHeight: `${boardHeight}px` }"
    >
      <div
        v-for="(note, index) in notes"
        :key="note.id || `note-${index}`"
        class="sticky-note"
        :class="{ 'sticky-note--dragging': draggingId === note.id }"
        :style="noteStyle(note, index)"
        @mousedown="startDrag(note, $event)"
      >
        <div class="sticky-note__pin"></div>
        <div class="sticky-note__body">
          {{ note.note }}
        </div>
      </div>

      <div v-if="!notes.length" class="sticky-board__empty">
        Suas próximas anotações aparecerão aqui como stickers.
      </div>
    </div>
  </div>
</template>

<script>
const NOTE_WIDTH = 220;
const NOTE_HEIGHT = 180;
const BOARD_PADDING = 24;
const NOTE_COLORS = [
  { background: "#fff4a8", shadow: "rgba(217, 119, 6, 0.2)", rotate: "-2deg" },
  { background: "#ffd6e7", shadow: "rgba(190, 24, 93, 0.18)", rotate: "1deg" },
  { background: "#d8f3dc", shadow: "rgba(22, 163, 74, 0.16)", rotate: "-1deg" },
  { background: "#dbeafe", shadow: "rgba(37, 99, 235, 0.16)", rotate: "2deg" },
];

export default {
  name: "StickyNotesBoard",

  props: {
    notes: {
      type: Array,
      default: () => [],
    },
    moduleKey: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      boardHeight: 360,
      layouts: {},
      draggingId: null,
      dragContext: null,
    };
  },

  watch: {
    notes: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLayouts();
      },
    },
  },

  beforeDestroy() {
    this.removeListeners();
  },

  methods: {
    defaultLayout(index) {
      const palette = NOTE_COLORS[index % NOTE_COLORS.length];

      return {
        x: BOARD_PADDING + (index % 3) * (NOTE_WIDTH + 18),
        y: BOARD_PADDING + Math.floor(index / 3) * (NOTE_HEIGHT + 18),
        background: palette.background,
        shadow: palette.shadow,
        rotate: palette.rotate,
      };
    },
    storageKey() {
      return `pitch-sticky-notes:${this.moduleKey}`;
    },
    readStoredLayouts() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey()) || "{}");
      } catch (error) {
        return {};
      }
    },
    saveLayouts() {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.layouts));
    },
    syncLayouts() {
      const storedLayouts = this.readStoredLayouts();
      const nextLayouts = {};

      this.notes.forEach((note, index) => {
        if (!note?.id) {
          return;
        }

        const fallbackLayout = this.defaultLayout(index);
        nextLayouts[note.id] = {
          x:
            typeof storedLayouts[note.id]?.x === "number"
              ? storedLayouts[note.id].x
              : fallbackLayout.x,
          y:
            typeof storedLayouts[note.id]?.y === "number"
              ? storedLayouts[note.id].y
              : fallbackLayout.y,
          background:
            storedLayouts[note.id]?.background || fallbackLayout.background,
          shadow: storedLayouts[note.id]?.shadow || fallbackLayout.shadow,
          rotate: storedLayouts[note.id]?.rotate || fallbackLayout.rotate,
        };
      });

      this.layouts = nextLayouts;
      this.saveLayouts();
      this.updateBoardHeight();
    },
    noteStyle(note, index) {
      const layout = this.layouts[note.id] || this.defaultLayout(index);

      return {
        width: `${NOTE_WIDTH}px`,
        minHeight: `${NOTE_HEIGHT}px`,
        transform: `translate(${layout.x}px, ${layout.y}px) rotate(${layout.rotate})`,
        background: layout.background,
        boxShadow: `0 20px 30px -20px ${layout.shadow}`,
      };
    },
    startDrag(note, event) {
      if (!note?.id || event.button !== 0) {
        return;
      }

      const boardRect = this.$refs.board?.getBoundingClientRect();
      const layout = this.layouts[note.id];

      if (!boardRect || !layout) {
        return;
      }

      this.draggingId = note.id;
      this.dragContext = {
        id: note.id,
        offsetX: event.clientX - boardRect.left - layout.x,
        offsetY: event.clientY - boardRect.top - layout.y,
      };

      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.dragContext || !this.$refs.board) {
        return;
      }

      const boardRect = this.$refs.board.getBoundingClientRect();
      const maxX = Math.max(boardRect.width - NOTE_WIDTH - BOARD_PADDING, 0);
      const nextX = Math.min(
        Math.max(event.clientX - boardRect.left - this.dragContext.offsetX, 0),
        maxX
      );
      const nextY = Math.max(
        event.clientY - boardRect.top - this.dragContext.offsetY,
        0
      );
      const layout = this.layouts[this.dragContext.id];

      if (!layout) {
        return;
      }

      this.$set(this.layouts, this.dragContext.id, {
        ...layout,
        x: nextX,
        y: nextY,
      });

      this.updateBoardHeight();
    },
    stopDrag() {
      if (!this.dragContext) {
        return;
      }

      this.draggingId = null;
      this.dragContext = null;
      this.saveLayouts();
      this.removeListeners();
    },
    removeListeners() {
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    updateBoardHeight() {
      const layouts = Object.values(this.layouts);

      if (!layouts.length) {
        this.boardHeight = 360;
        return;
      }

      const tallestPoint = layouts.reduce((max, item) => {
        return Math.max(max, (item?.y || 0) + NOTE_HEIGHT + BOARD_PADDING);
      }, 0);

      this.boardHeight = Math.max(tallestPoint, 360);
    },
  },
};
</script>

<style scoped>
.sticky-board__toolbar {
  margin-bottom: 1rem;
}

.sticky-board__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.sticky-board__subtitle {
  margin-top: 0.2rem;
  color: rgba(31, 41, 55, 0.68);
}

.sticky-board {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  border: 1px dashed rgba(236, 72, 153, 0.24);
  background: radial-gradient(
      circle at 20% 20%,
      rgba(255, 255, 255, 0.65),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.96) 0%,
      rgba(251, 207, 232, 0.18) 100%
    );
}

.sticky-note {
  position: absolute;
  top: 0;
  left: 0;
  padding: 1.25rem 1rem 1rem;
  border-radius: 18px;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.sticky-note--dragging {
  cursor: grabbing;
  z-index: 4;
}

.sticky-note__pin {
  position: absolute;
  top: 12px;
  right: 14px;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.68);
  box-shadow: 0 6px 12px -6px rgba(15, 23, 42, 0.45);
}

.sticky-note__body {
  white-space: pre-wrap;
  color: #1f2937;
  font-weight: 600;
  line-height: 1.45;
}

.sticky-board__empty {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: rgba(31, 41, 55, 0.58);
}

@media (max-width: 700px) {
  .sticky-note {
    width: calc(100% - 32px) !important;
    min-height: 160px !important;
  }
}
</style>
